import { useEffect, useRef, useState } from 'react';
import TopBar from '../ui/TopBar.js';
import { useApp } from '../state.js';

type TestId = 'A' | 'B' | 'C' | 'D';

interface Test {
  id: TestId;
  title: string;
  desc: string;
  layers: number;
  desync: boolean;
}

/**
 * Quatro combinações de canvas, da mais simples à que o caderno usa.
 * Olhando quais quadrados ficam pretos dá para saber exatamente o que o driver
 * do aparelho recusa — coisa que nenhum log do servidor mostra.
 */
const TESTS: Test[] = [
  { id: 'A', title: 'Canvas simples', desc: 'Um canvas só, comum.', layers: 1, desync: false },
  { id: 'B', title: 'Duas camadas', desc: 'Um canvas branco com outro transparente por cima.', layers: 2, desync: false },
  { id: 'C', title: 'Baixa latência', desc: 'Um canvas só, com desynchronized.', layers: 1, desync: true },
  { id: 'D', title: 'Camadas + baixa latência', desc: 'É o que o caderno usa para escrever.', layers: 2, desync: true },
];

function Patch({ test, onResult }: { test: Test; onResult(id: TestId, granted: boolean): void }): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = ref.current;
    if (!host) return;
    host.replaceChildren();
    const w = host.clientWidth || 300;
    const h = 96;
    let granted = true;

    const make = (z: number, desync: boolean): CanvasRenderingContext2D | null => {
      const c = document.createElement('canvas');
      c.width = Math.round(w * Math.min(window.devicePixelRatio || 1, 2.5));
      c.height = Math.round(h * Math.min(window.devicePixelRatio || 1, 2.5));
      c.style.cssText = `position:absolute;inset:0;width:${w}px;height:${h}px;z-index:${z}`;
      host.append(c);
      let ctx: CanvasRenderingContext2D | null = null;
      if (desync) {
        try { ctx = c.getContext('2d', { desynchronized: true, alpha: true }); } catch { ctx = null; }
        if (!ctx) granted = false;
      }
      if (!ctx) ctx = c.getContext('2d', { alpha: true });
      if (ctx) ctx.scale(c.width / w, c.height / h);
      return ctx;
    };

    if (test.layers === 1) {
      const ctx = make(0, test.desync);
      if (ctx) {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, w, h);
        ctx.fillStyle = '#1F47C9';
        ctx.fillRect(12, 12, 48, 48);
        ctx.fillStyle = '#15213A';
        ctx.font = '14px system-ui';
        ctx.fillText('deve ver isto', 74, 40);
      }
    } else {
      const base = make(0, false);
      if (base) {
        base.fillStyle = '#FFFFFF';
        base.fillRect(0, 0, w, h);
        base.fillStyle = '#15213A';
        base.font = '14px system-ui';
        base.fillText('camada de baixo', 12, 76);
      }
      const top = make(1, test.desync);
      if (top) {
        top.fillStyle = '#C0352B';
        top.fillRect(12, 12, 48, 48);
        top.fillStyle = '#15213A';
        top.font = '14px system-ui';
        top.fillText('camada de cima', 74, 40);
      }
    }
    onResult(test.id, granted);
  }, [test, onResult]);

  return (
    <div className="field">
      <span className="label">{test.id}. {test.title}</span>
      <span className="hint">{test.desc}</span>
      <div ref={ref} style={{ position: 'relative', height: 96, background: '#8D97A5', borderRadius: 8, overflow: 'hidden' }} />
    </div>
  );
}

export default function Diag(): JSX.Element {
  const showToast = useApp((s) => s.showToast);
  const [granted, setGranted] = useState<Record<string, boolean>>({});
  const [info, setInfo] = useState<Record<string, unknown>>({});

  useEffect(() => {
    const probe = document.createElement('canvas');
    let attrs: unknown = null;
    try {
      attrs = probe.getContext('2d', { desynchronized: true })?.getContextAttributes?.() ?? null;
    } catch (err) {
      attrs = `erro: ${String(err)}`;
    }
    setInfo({
      build: typeof __BUILD__ === 'string' ? __BUILD__ : '(desconhecido)',
      navegador: navigator.userAgent,
      dpr: window.devicePixelRatio,
      tela: `${screen.width}×${screen.height}`,
      janela: `${innerWidth}×${innerHeight}`,
      memoriaGb: (navigator as { deviceMemory?: number }).deviceMemory ?? '(não informado)',
      pontoDeToque: navigator.maxTouchPoints,
      contextoSeguro: isSecureContext,
      atributosConcedidos: attrs,
    });
  }, []);

  const texto = JSON.stringify({ ...info, desynchronizedConcedido: granted }, null, 2);

  return (
    <>
      <TopBar title="Diagnóstico" back="/settings" />
      <div className="content">
        <div className="settings">
          <section>
            <header><h2>Teste de canvas</h2></header>
            <p className="desc">
              Cada quadrado abaixo deve mostrar um desenho sobre fundo branco. <strong>Me diga
              quais ficam pretos ou vazios</strong> — é isso que identifica o que o seu tablet recusa.
            </p>
            <div className="body">
              {TESTS.map((t) => (
                <Patch
                  key={t.id}
                  test={t}
                  onResult={(id, ok) => setGranted((g) => (g[id] === ok ? g : { ...g, [id]: ok }))}
                />
              ))}
            </div>
          </section>

          <section>
            <header><h2>Este aparelho</h2></header>
            <div className="body">
              {Object.entries(info).map(([k, v]) => (
                <div className="kv" key={k}>
                  <span>{k}</span>
                  <span style={{ maxWidth: '60%', textAlign: 'right', wordBreak: 'break-word' }}>
                    {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                  </span>
                </div>
              ))}
              <hr className="divider" />
              <button
                onClick={() => {
                  void navigator.clipboard?.writeText(texto)
                    .then(() => showToast('Diagnóstico copiado.'))
                    .catch(() => showToast('Não consegui copiar; tire um print desta tela.'));
                }}
              >
                Copiar diagnóstico
              </button>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
